import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { fmtSGD } from "@/lib/format";
import { RECOVERY } from "@/constants/testIds";
import { ShieldAlert, CheckCircle2, Clock, CircleDashed } from "lucide-react";

const STATUS = {
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", Icon: CheckCircle2 },
  in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500", Icon: Clock },
  planned: { label: "Planned", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400", Icon: CircleDashed },
};
const ORDER = { resolved: 0, in_progress: 1, planned: 2 };

function driverStatus(it, defectById) {
  const d = it.defect_id ? defectById[it.defect_id] : null;
  if (d && d.status === "resolved") return "resolved";
  if (it.status === "approved") return "in_progress";
  return "planned";
}

export function RecoveryTracker({ contract }) {
  const [drivers, setDrivers] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const siteIds = contract?.site_ids || [];
      if (!contract?.at_risk || siteIds.length === 0) { setDrivers([]); return; }
      try {
        const [defLists, intLists] = await Promise.all([
          Promise.all(siteIds.map((sid) => api.get(`/defects?site_id=${sid}`))),
          Promise.all(siteIds.map((sid) => api.get(`/interventions?site_id=${sid}`))),
        ]);
        const defects = defLists.flatMap((r) => r.data);
        const interventions = intLists.flatMap((r) => r.data);
        const defectById = Object.fromEntries(defects.map((d) => [d.id, d]));
        const rows = interventions.map((it) => ({ ...it, _status: driverStatus(it, defectById) }));
        if (active) setDrivers(rows);
      } catch {
        if (active) setDrivers([]);
      }
    })();
    return () => { active = false; };
  }, [contract]);

  if (!contract?.at_risk || !drivers || drivers.length === 0) return null;

  const counts = { resolved: 0, in_progress: 0, planned: 0 };
  let totalCost = 0, recoveredCost = 0;
  drivers.forEach((d) => {
    counts[d._status] = (counts[d._status] || 0) + 1;
    const c = d.est_cost_sgd || 0;
    totalCost += c;
    if (d._status === "resolved") recoveredCost += c;
  });
  const pct = Math.round((counts.resolved / drivers.length) * 100);
  const sorted = [...drivers].sort((a, b) => ORDER[a._status] - ORDER[b._status] || (b.priority_score || 0) - (a.priority_score || 0));

  return (
    <Card data-testid={RECOVERY.root} className="border border-sage-light border-l-4 border-l-terracotta p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark">
            <ShieldAlert className="h-4 w-4 text-terracotta" /> Recovery tracker
          </h3>
          <p className="text-xs text-muted-foreground">Cost-recovery plan for {contract.name} — each driver tracked from open to resolved.</p>
        </div>
        <div className="text-right">
          <p data-testid={RECOVERY.pct} className="font-heading text-3xl font-bold text-forest-dark">{pct}%</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{counts.resolved} of {drivers.length} resolved</p>
        </div>
      </div>

      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-sage-surface">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {["resolved", "in_progress", "planned"].map((k) => (
          <div key={k} data-testid={RECOVERY.stat(k)} className="rounded-xl border border-sage-light bg-sage-surface p-2.5 text-center">
            <p className="font-heading text-lg font-bold text-forest-dark">{counts[k]}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{STATUS[k].label}</p>
          </div>
        ))}
        <div className="rounded-xl border border-sage-light bg-sage-surface p-2.5 text-center">
          <p className="font-heading text-lg font-bold text-forest-dark">{fmtSGD(recoveredCost)}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">of {fmtSGD(totalCost)} recovered</p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {sorted.map((d) => {
          const st = STATUS[d._status];
          const Icon = st.Icon;
          return (
            <div key={d.id} data-testid={RECOVERY.row(d.id)} className="flex items-center gap-3 rounded-lg border border-sage-light bg-white p-3">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${st.dot}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-forest-dark">{d.title}</p>
                <p className="text-xs text-muted-foreground">{fmtSGD(d.est_cost_sgd)}</p>
              </div>
              <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                <Icon className="h-3 w-3" /> {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
